import { Editor } from "components/document-text-area/elements/editor"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import {
	createPlateEditor,
	Plate,
	PlateEditor,
	PlateLeaf,
	TElement,
} from "@udecode/plate-common"
import { plugins } from "../constants/plate"
import { deserializeMd, serializeMd } from "@udecode/plate-serializer-md"
import { ELEMENT_PARAGRAPH } from "@udecode/plate-paragraph"
import { useLegalDocumentContentMutation } from "hooks/useLegalDocumentContentMutation"
import {
	LegalDocumentType,
	RetrieveLegalDocumentResponse,
} from "types/legalDocument"
import _ from "lodash"
import { FloatingToolbar } from "components/document-text-area/elements/floating-toolbar"
import { FloatingToolbarButtons } from "components/document-text-area/elements/floating-toolbar-buttons"
import { Toolbar } from "components/document-text-area/elements/toolbar"
import { withCn } from "@udecode/cn"
import LexAssistant from "pages/AnalizeDocumentPage/subcomponents/LexAssistant"
import { openLexAtom } from "pages/SidebarPages/subcomponents/atoms"
import { useAtom } from "jotai"

import DocumentHeader from "../subcomponents/Header/DocumentHeader"
import { blockQuoteMarginAtom } from "pages/DocumentPage/atoms"
import { RetrieveLegalDocumentResponse as RetrieveLegalDocumentResponseV2 } from "types/legalDocumentV2"
import { motion, AnimatePresence } from "framer-motion"
import LoadingSpinner from "pages/MyDocumentsPage/subcomponents/LoadingSpinner"
import Flex from "components/flex"

export const FixedToolbar = withCn(
	Toolbar,
	"supports-backdrop-blur:bg-background/60 gap-2 h-[24px]",
)

const LOADING_TEXTS = [
	"Analisando os detalhes do caso...",
	"Definindo o tipo de ação...",
	"Estruturando os argumentos jurídicos...",
	"Buscando jurisprudência em tribunais...",
	"Revisando argumentação e escrita...",
	"Finalizando os últimos detalhes...",
]

export default function DocumentCompleted({
	documentId,
	graphExecutionStatus,
	legalDocument,
}: {
	documentId: string
	graphExecutionStatus: "SUCCESS" | "START" | "ERROR"
	legalDocument:
		| RetrieveLegalDocumentResponseV2
		| RetrieveLegalDocumentResponse
		| undefined
}) {
	const [content, setContent] = useState(" ")
	const [isLoading, setIsLoading] = useState(true)
	const [zoomLevel, setZoomLevel] = useState(2)
	const [lexOpen] = useAtom(openLexAtom)
	const [, setBlockQuoteMargin] = useAtom(blockQuoteMarginAtom)
	const editor = createPlateEditor({ plugins })
	const textRef = useRef<HTMLDivElement>(null)
	const editorRef = useRef<PlateEditor | null>(null)
	const isInitialMount = useRef(true)
	const [loadingTextIndex, setLoadingTextIndex] = useState(0)

	const [valueContent, setValueContent] = useState(deserializeMd(editor, ""))

	const updateTextMutation = useLegalDocumentContentMutation(
		documentId,
		legalDocument?.type as LegalDocumentType,
	)
	const paperRef = useRef<HTMLDivElement>(null)

	const processingTextClasses =
		graphExecutionStatus === "START" ? "leading-6 text-brand-gray-2" : ""

	const debouncedOnChange = _.debounce((value) => {
		const payload = { text: value }
		updateTextMutation.mutate({
			id: documentId,
			payload,
		})
	}, 500)

	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false
		} else {
			debouncedOnChange(content)
		}

		return () => {
			debouncedOnChange.cancel()
		}
	}, [content])

	useEffect(() => {
		let timer: NodeJS.Timeout
		if (loadingTextIndex < LOADING_TEXTS.length - 1) {
			timer = setTimeout(() => {
				setLoadingTextIndex((prev) => prev + 1)
			}, 6000)
		}
		return () => clearTimeout(timer)
	}, [loadingTextIndex])

	useLayoutEffect(() => {
		if (!paperRef.current) return
		if (!textRef.current) return

		const updateStyles = (width: number) => {
			if (paperRef.current && zoomLevel === 1) {
				const fontSize = `${width / 730}em`
				setBlockQuoteMargin(`${width / 7.5}px`)
				paperRef.current.style.fontSize = fontSize
			} else if (paperRef.current) {
				setBlockQuoteMargin(`8em`)
				paperRef.current.style.fontSize = ""
			}
		}

		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				updateStyles(entry.contentRect.width)
			}
		})

		observer.observe(paperRef.current)

		updateStyles(paperRef.current.clientWidth)

		return () => {
			if (paperRef.current) observer.unobserve(paperRef.current)
		}
	}, [zoomLevel, paperRef.current, textRef.current])

	function escapeListNumbersAndRemoveBullets(text: string): string {
		return text
			.replace(/^>\s*(\d)\.\s*/gm, (match, p1) => `> 0${p1}\\. `)
			.replace(/^-\s*([a-zA-Z]\))/gm, "$1")
	}

	useEffect(() => {
		if (editor && legalDocument?.content?.text) {
			setValueContent(
				deserializeMd(
					editor,
					escapeListNumbersAndRemoveBullets(
						legalDocument?.content?.text,
					) ?? "",
				).map((node: TElement) => ({
					type: node.type ?? ELEMENT_PARAGRAPH,
					children:
						!node.children || node.children.length === 0
							? [{ text: "" }]
							: node.children,
				})),
			)
			setContent(legalDocument?.content?.text)
			setIsLoading(false)
		}
	}, [legalDocument?.content?.text, editorRef.current])

	function onZoomText() {
		if (zoomLevel === 1) {
			return ""
		}
		if (zoomLevel === 2) {
			return "!text-[1em] "
		}

		if (zoomLevel === 3) {
			return "!text-[1.25em] "
		}

		if (zoomLevel === 4) {
			return "!text-[1.6em]"
		}

		if (zoomLevel === 5) {
			return "!text-[2em]"
		}

		return "!text-[1em]"
	}

	function onZoomWidthEditor() {
		if (zoomLevel === 1) {
			return "!w-full !max-w-[100%]"
		} else {
			return "max-w-[46em] min-w-[46em] "
		}
	}

	function onZoomContainer() {
		if (!lexOpen) {
			return "w-full"
		}
		if (zoomLevel === 1) {
			return "!w-full"
		}

		if (zoomLevel === 3) {
			return "w-[70vw]"
		}
		if (zoomLevel === 4) {
			return "w-[80vw]"
		}
		if (zoomLevel === 5) {
			return "w-[90vw] "
		}

		return "w-[calc(100vw-492px)]"
	}

	function getMinWidth() {
		if (zoomLevel === 1) {
			return "min-w-[960px]"
		}
		if (zoomLevel === 2) {
			return "!min-w-[753px]"
		}
		if (zoomLevel === 3) {
			return "!min-w-[970px]"
		}
		if (zoomLevel === 4) {
			return "!min-w-[1205px]"
		}
		if (zoomLevel === 5) {
			return "!min-w-[1472px]"
		}
		return ""
	}

	const zoomText = onZoomText()
	const zoomWidthEditor = onZoomWidthEditor()
	const zoomContainer = onZoomContainer()
	const minWidth = getMinWidth()

	if (!legalDocument) return

	if (isLoading || !valueContent || valueContent.length === 0) {
		return (
			<>
				<DocumentHeader
					documentId={documentId}
					data={legalDocument}
					setZoomLevel={setZoomLevel}
					zoomLevel={zoomLevel}
					whithoutPlate={true}
					disabled
					status={graphExecutionStatus}
				/>
				<div className="flex items-center justify-center w-full">
					<div className="bg-white mt-[24px] w-full max-w-[797px]">
						<div className="mb-6 px-[113px] py-[95px] relative overflow-hidden h-16">
							<Flex className="relative">
								<div className="bg-gradient0 absolute top-[-42px] left-[0] w-full h-[40px] z-50" />
								<LoadingSpinner />
								<div className="relative h-full w-full">
									<AnimatePresence mode="wait">
										<motion.p
											key={loadingTextIndex}
											initial={{ y: 50, opacity: 0 }}
											animate={{ y: 0, opacity: 1 }}
											exit={{ y: -50, opacity: 0 }}
											transition={{ duration: 0.3 }}
											className="absolute left-[16px] bottom-[-16px] w-full text-[#6D6D6D]"
										>
											{LOADING_TEXTS[loadingTextIndex]}
										</motion.p>
									</AnimatePresence>
								</div>
								<div className="bg-gradient180 absolute top-[30px] left-[0] w-full h-[40px]" />
							</Flex>
						</div>
					</div>
				</div>
			</>
		)
	}

	return (
		<div className=" bg-ice-white font-lato">
			<Plate
				key={"success"}
				plugins={plugins}
				editorRef={editorRef}
				initialValue={valueContent}
				onChange={(newValue) => {
					setContent(serializeMd(editorRef.current))
					setValueContent(newValue)
				}}
			>
				<DocumentHeader
					documentId={documentId}
					data={legalDocument}
					setZoomLevel={setZoomLevel}
					zoomLevel={zoomLevel}
					status={graphExecutionStatus}
				/>

				<div className="flex w-screen">
					<div
						className={`p-6 h-[calc(100vh-96px)] overflow-auto w-full `}
					>
						<div
							className={`${zoomContainer} ${minWidth}  flex items-center justify-center text-[18px] ${zoomText} ${
								!textRef.current && "hidden"
							} `}
							ref={paperRef}
						>
							<Editor
								ref={textRef}
								className={`editor-root [&_ul]:list-disc
														[&_ul]:list-inside
														[&_ul]:pl-6
														[&_ul]:ml-0  [&_li]:list-item
														[&_li]:ml-6 [&_li>div[data-slate-node="element"]]:contents 
														w-full px-[6.3em] py-[5.3em] ${zoomWidthEditor}  bg-white text-justify shadow-md outline-none !focus-visible:ring-0 !focus-visible:ring-ring !focus-visible:ring-offset-0 ${processingTextClasses} !font-Times-New-Roman`}
								style={{
									letterSpacing: "0.02em",
								}}
								value={content}
								readOnly={graphExecutionStatus === "START"}
								disableDefaultStyles
								renderLeaf={(props) => (
									<PlateLeaf
										{...props}
										editor={editor}
										style={{}}
									/>
								)}
							/>
						</div>
					</div>

					<LexAssistant isDocumentPage />
				</div>
				<FloatingToolbar>
					<FloatingToolbarButtons />
				</FloatingToolbar>
			</Plate>
		</div>
	)
}
